import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RunStep } from "~/api/types";
import { compactTokens, formatDuration } from "~/features/automation/format";
import { statusPresentation } from "~/features/automation/status";
import { clockTime, metaString, stepTokens } from "~/features/runs/runDerive";
import { Empty } from "~/foundation/components/Empty";
import { cn } from "~/foundation/utils/cn";

/**
 * Step status colours. Kept apart from `RUN_STATUS_COLORS` because the vocabularies differ:
 * a *step* can be `pending` or `skipped`, a run never is, and a shared table would have to
 * invent a colour for a word one of them cannot say.
 */
const STEP_DOT: Record<string, string> = {
  pending: "#8a8f98",
  skipped: "#8a8f98",
  cancelled: "#8a8f98",
  running: "#2f80ed",
  completed: "#1f9d55",
  failed: "#d64545",
};

function StepCard({ step }: { step: RunStep }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(step.status === "running" || step.status === "failed");
  const previousStatus = useRef(step.status);

  // A step that starts running while the reader is looking at the run opens itself: the whole
  // point of the tab is that the node currently doing work is the one on screen.
  useEffect(() => {
    if (previousStatus.current !== "running" && step.status === "running") {
      setOpen(true);
    }
    previousStatus.current = step.status;
  }, [step.status]);

  const presentation = statusPresentation(step.status, t);
  const model = metaString(step.meta, "model");
  const tokens = stepTokens(step);
  const prompt = metaString(step.meta, "prompt");
  const promptChars =
    typeof step.meta?.promptChars === "number" ? step.meta.promptChars : (prompt?.length ?? 0);

  return (
    <li className="rounded-md border border-divider">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-start hover:bg-hover-bg"
      >
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: STEP_DOT[step.status] ?? STEP_DOT.pending }}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-medium text-text">{step.name}</span>
          <span className="block text-small text-text-tertiary">
            {[
              presentation.label,
              model,
              step.durationMs === null ? null : formatDuration(step.durationMs, t),
              tokens === null ? null : t("{{tokens}} tok", { tokens: compactTokens(tokens) }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
        <span aria-hidden className={cn("shrink-0 text-text-tertiary", open && "rotate-90")}>
          <span className="rtl:rotate-180 inline-block">›</span>
        </span>
      </button>

      {open ? (
        <div className="flex flex-col gap-2 border-t border-divider px-3 py-2.5">
          {prompt ? (
            <details className="rounded-sm bg-bg-secondary px-2 py-1.5">
              {/* A node held BEFORE it runs has no output — the prompt is the only answer to
                  "what is this thing about to do?", which is the question the Approve button
                  above is really asking. */}
              <summary className="cursor-pointer text-small text-text-secondary">
                {t("Prompt this node received · {{count}} chars", { count: promptChars })}
              </summary>
              <pre
                dir="ltr"
                className="mt-1.5 whitespace-pre-wrap break-words font-mono text-small text-text"
              >
                {prompt}
              </pre>
            </details>
          ) : null}

          {step.output ? (
            <pre
              dir="ltr"
              className="whitespace-pre-wrap break-words font-mono text-small text-text"
            >
              {step.output}
              {step.outputTruncated ? (
                <span className="text-text-tertiary">{t("… (truncated)")}</span>
              ) : null}
            </pre>
          ) : null}

          {step.error ? (
            <pre
              dir="ltr"
              className="whitespace-pre-wrap break-words font-mono text-small text-danger"
            >
              {step.error}
            </pre>
          ) : null}

          {step.log.length > 0 ? (
            <ul dir="ltr" className="flex flex-col font-mono text-small text-text-secondary">
              {step.log.map((line) => (
                <li key={`${line.at}-${line.message}`}>
                  <span className="text-text-tertiary">{clockTime(line.at)}</span> {line.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function RunStepsTab({ steps }: { steps: RunStep[] }) {
  const { t } = useTranslation();
  if (steps.length === 0) {
    return <Empty message={t("This run has not recorded a step yet.")} />;
  }
  const ordered = [...steps].sort((a, b) => a.index - b.index);
  return (
    <ul className="flex flex-col gap-2">
      {ordered.map((step) => (
        <StepCard key={`${step.index}-${step.nodeId}`} step={step} />
      ))}
    </ul>
  );
}
